import './index.css'

import React from 'react'
import { createRoot } from 'react-dom/client'

import { BrowserRouter } from 'react-router-dom'

import App from '@/App'
import AppWrapper from '@/AppWrapper'
import { msalConfig } from '@/authConfig'
import { initDatadog } from '@/utils/datadog'
import type { AuthenticationResult, EventMessage } from '@azure/msal-browser'
import { EventType, PublicClientApplication } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

// Initialize Datadog RUM before app renders
initDatadog()

export const msalInstance = new PublicClientApplication(msalConfig)

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
      refetchOnWindowFocus: true,
      staleTime: 1000 * 60 * 1, // tbc if we need it and what value it should have
    },
  },
})

const container = document.getElementById('root')
const root = createRoot(container!)

msalInstance.initialize().then(async () => {
  await msalInstance.handleRedirectPromise().catch((error) => {
    console.error('Error handling redirect promise:', error)
    if (error.errorCode === 'interaction_in_progress') {
      msalInstance.clearCache()
    }
  })

  const accounts = msalInstance.getAllAccounts()
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0])
  }

  msalInstance.addEventCallback((event: EventMessage) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as AuthenticationResult
      msalInstance.setActiveAccount(payload.account)
    }
  })

  root.render(
    <React.StrictMode>
      <BrowserRouter>
        <MsalProvider instance={msalInstance}>
          <QueryClientProvider client={queryClient}>
            <AppWrapper>
              <App />
            </AppWrapper>
          </QueryClientProvider>
        </MsalProvider>
      </BrowserRouter>
    </React.StrictMode>,
  )
})
